import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Container,
    Typography,
    Divider,
    TextField,
    Autocomplete,
    Chip,
    InputAdornment,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    IconButton,
} from "@mui/material";

import queryGraphql from "../../../helpers/ApiInterface";
import { ExpandMore } from "@mui/icons-material";
import MainDrawer from "../../MainDrawer";
import RAGRequestsTable from "./RAGRequestsTable"; // Renamed import
import MiniBot from "../../MiniBot";

interface SearchOptions {
    chipLabel: string;
    dropdownLabel: string;
    category: string;
    groupBy: string;
}

let searchOptions: SearchOptions[] = [];

const RAGRequestsPage: React.FC = () => {
    const [rags, setRags] = useState<any[]>([]); // State variable for RAG data
    const [searchQuery, setSearchQuery] = useState<SearchOptions[]>([]);
    const [updates, setUpdates] =  useState<any[]>([]);
    const navigate = useNavigate();
    React.useEffect(() =>{
        const fetchData = async () => {
            try {
                const data = await queryGraphql(`
                        query Query {
                        resourceStatuses {
                            id
                            currentStatus
                            resourceId
                        }
                        }
                    `);
                if(data.resourceStatuses){
                    setUpdates(data.resourceStatuses);
                }
                }
                catch(e){
            console.log(e);
                }
    }
    fetchData();
    },[]);  
    
    React.useEffect(() => {
        const fetchData = async () => {
            try {
                const data = await queryGraphql(`
                    query GetRAGs {
                      getRAGs {
                        id
                        name
                        solutionType
                        solutionCategoryType
                        cloudProvider
                        environment
                        status
                        details {
                          rag {
                            projectId
                            databaseType
                            embeddingModelId
                            region
                          }
                        }
                        reasonForRejection
                        createdAt
                        updatedAt
                        solutionOwner {
                          id
                          name
                          email
                          googleProfilePicture
                        }
                        solutionApprover {
                          id
                          name
                          email
                          googleProfilePicture
                        }
                        application {
                          id
                          applicationName
                          environment
                        }
                      }
                    }
                `);

                const rawRAGsData = data.data?.getRAGs || data.getRAGs || [];

                let ragsArr = rawRAGsData.map((e: any) => {
                    return {
                        id: e.id,
                        ragName: e.name,
                        applicationName: e.application?.applicationName,
                        status: e.status ? e.status.toLowerCase() : 'pending',
                        createdAt: e.createdAt ? new Date(Number(e.createdAt)).toISOString() : null,
                        actionedAt: e.updatedAt ? new Date(Number(e.updatedAt)).toISOString() : null,
                        
                        ragOwnerName: e.solutionOwner?.name,
                        ragOwnerAvatar: e.solutionOwner?.googleProfilePicture,
                        
                        ragApproverName: e.solutionApprover?.name,
                        ragApproverAvatar: e.solutionApprover?.googleProfilePicture,

                        cloudProvider: e.cloudProvider,
                        solutionCategoryType: e.solutionCategoryType,
                        projectId: e.details?.rag?.projectId,
                        reasonForRejection: e.reasonForRejection,
                    };
                });
                setRags(ragsArr);

                searchOptions = ragsArr
                    .map((e: any) => {
                        const options = [
                            {
                                category: "ragName",
                                groupBy: "RAG NAME",
                                chipLabel: `RAG: ${e.ragName}`,
                                dropdownLabel: e.ragName,
                            },
                            {
                                category: "ragOwnerName",
                                groupBy: "OWNER",
                                chipLabel: `Owner: ${e.ragOwnerName}`,
                                dropdownLabel: e.ragOwnerName,
                            },
                            {
                                category: "ragApproverName",
                                groupBy: "APPROVER",
                                chipLabel: `Approver: ${e.ragApproverName}`,
                                dropdownLabel: e.ragApproverName,
                            },
                        ];
                        if (e.applicationName) {
                             options.push({
                                category: "applicationName",
                                groupBy: "APPLICATION NAME",
                                chipLabel: `App: ${e.applicationName}`,
                                dropdownLabel: e.applicationName,
                            });
                        }
                        if (e.cloudProvider) {
                            options.push({
                               category: "cloudProvider",
                               groupBy: "CLOUD PROVIDER",
                               chipLabel: `Provider: ${e.cloudProvider}`,
                               dropdownLabel: e.cloudProvider,
                           });
                       }
                        if (e.solutionCategoryType) {
                            options.push({
                               category: "solutionCategoryType",
                               groupBy: "CATEGORY TYPE",
                               chipLabel: `Category: ${e.solutionCategoryType}`,
                               dropdownLabel: e.solutionCategoryType,
                           });
                       }
                        if (e.status) {
                            options.push({
                                category: "status",
                                groupBy: "STATUS",
                                chipLabel: `Status: ${e.status.charAt(0).toUpperCase() + e.status.slice(1)}`,
                                dropdownLabel: e.status.charAt(0).toUpperCase() + e.status.slice(1),
                            });
                        }
                        return options;
                    })
                    .flat();

                searchOptions = searchOptions.filter(
                    (obj, index, self) =>
                        obj.dropdownLabel &&
                        index ===
                        self.findIndex(
                            (t) => t.category === obj.category && t.dropdownLabel === obj.dropdownLabel
                        )
                );
                searchOptions.sort((a, b) => {
                    if (a.groupBy > b.groupBy) return 1;
                    if (a.groupBy < b.groupBy) return -1;
                    if (a.dropdownLabel > b.dropdownLabel) return 1;
                    if (a.dropdownLabel < b.dropdownLabel) return -1;
                    return 0;
                });

            } catch (error) {
                console.log(error);
            }
        };

        fetchData();
    }, []);

    const filteredRAGs =
        searchQuery.length === 0
            ? rags
            : rags.filter(
                    (rag: any) =>
                        (searchQuery.filter((e) => e.category === "ragName").length > 0
                            ? searchQuery.filter((e) => e.category === "ragName").map((e) => e.dropdownLabel?.toLowerCase()).includes(rag.ragName?.toLowerCase())
                            : true) &&
                        (searchQuery.filter((e) => e.category === "ragOwnerName").length > 0
                            ? searchQuery.filter((e) => e.category === "ragOwnerName").map((e) => e.dropdownLabel?.toLowerCase()).includes(rag.ragOwnerName?.toLowerCase())
                            : true) &&
                         (searchQuery.filter((e) => e.category === "ragApproverName").length > 0
                            ? searchQuery.filter((e) => e.category === "ragApproverName").map((e) => e.dropdownLabel?.toLowerCase()).includes(rag.ragApproverName?.toLowerCase())
                            : true) &&
                        (searchQuery.filter((e) => e.category === "applicationName").length > 0
                            ? searchQuery.filter((e) => e.category === "applicationName").map((e) => e.dropdownLabel?.toLowerCase()).includes(rag.applicationName?.toLowerCase())
                            : true) &&
                        (searchQuery.filter((e) => e.category === "cloudProvider").length > 0
                            ? searchQuery.filter((e) => e.category === "cloudProvider").map((e) => e.dropdownLabel?.toLowerCase()).includes(rag.cloudProvider?.toLowerCase())
                            : true) &&
                        (searchQuery.filter((e) => e.category === "solutionCategoryType").length > 0
                            ? searchQuery.filter((e) => e.category === "solutionCategoryType").map((e) => e.dropdownLabel?.toLowerCase()).includes(rag.solutionCategoryType?.toLowerCase())
                            : true) &&
                        (searchQuery.filter((e) => e.category === "status").length > 0
                            ? searchQuery.filter((e) => e.category === "status").map((e) => e.dropdownLabel?.toLowerCase()).includes(rag.status?.toLowerCase())
                            : true)
              );
    
    const filteredActiveRAGs = filteredRAGs.filter(
        (p: any) => p.status === "active" || p.status === "approved"
    );
    const filteredPendingRAGs = filteredRAGs.filter(
        (p: any) => p.status === "pending"
    );
    const filteredRejectedRAGs = filteredRAGs.filter(
        (p: any) => p.status === "rejected"
    );

    return (
        <Box sx={{ display: "flex" }}>
            <MainDrawer index={"requests-rag"} />
            <MiniBot/>
            <Box
                component="main"
                sx={{
                    flex: 1,
                    bgcolor: "#fafafa",
                    minHeight: window.innerHeight,
                }}
            >
                <Container maxWidth={false} disableGutters>
                    <Box bgcolor="#FFFFFF" p={2} mb="18px">
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginY: 1,
                                marginLeft: 2,
                            }}
                        >
                            <Typography fontSize="22px" gutterBottom fontFamily="Gilroy-Bold">
                                Requests
                            </Typography>
                            <IconButton
                                aria-label="Help" 
                                onClick={()=> window.open(`${process.env.REACT_APP_HOST_DOMAIN}/docs` , "_blank")}
                                sx={{ padding: 0, border: 'none', mr:2, '&:hover': { backgroundColor: 'action.hover' } }}
                              >
                                <Box component="img" src="/assets/Help.svg" alt="" sx={{ width: 40,  height: 40, display: 'block' }} />
                              </IconButton>
                        </Box>
                        <Divider sx={{ marginY: 1 }} />
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mx: 2 }}>
                            <Box>
                                <Typography fontFamily="Gilroy-SemiBold" fontSize="16px">
                                    {`RAGs (${rags.length})`}
                                </Typography>
                                <Typography fontFamily="Gilroy-Regular" fontSize="14px">
                                    {`View all the requests raised for RAG Solutions`}
                                </Typography>
                            </Box>
                            <Autocomplete
                                multiple
                                options={searchOptions}
                                filterSelectedOptions
                                getOptionLabel={(option) => option.dropdownLabel}
                                defaultValue={[]}
                                groupBy={(option) => option.groupBy}
                                renderTags={(tagValue, getTagProps) => {
                                    return tagValue.map((option, index) => (
                                        <Chip {...getTagProps({ index })} size="small" label={option.chipLabel} />
                                    ));
                                }}
                                onChange={(e, newVal, reason) => {
                                    setSearchQuery(newVal);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="filled"
                                        placeholder="Search by RAG name, owner, status etc"
                                        fullWidth
                                        size="small"
                                        hiddenLabel
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <>
                                                    <InputAdornment position="start">
                                                        <img src="/assets/search-normal.svg" alt="Filter Icon" style={{ width: "16px", paddingLeft: "4px" }}/>
                                                    </InputAdornment>
                                                    {params.InputProps.startAdornment}
                                                </>
                                            ),
                                            disableUnderline: true,
                                            style: { backgroundColor: "#f4f4f4", minHeight: "45px", width: "500px", borderRadius: "8px", fontFamily: "Gilroy-Regular", fontSize: "14px" },
                                        }}
                                    />
                                )}
                            />
                        </Box>
                    </Box>
                    <Box m={2}>
                        <Accordion
                            defaultExpanded={filteredPendingRAGs.length > 0 ? true : false}
                            elevation={0}
                            sx={{ borderRadius: "8px", mb: "16px", "&:before": { display: "none" }, bgcolor: "#fafafa" }}
                            disabled={filteredPendingRAGs.length === 0}
                        >
                            <AccordionSummary expandIcon={<ExpandMore />}>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <img src="/assets/clock.svg" alt="Pending Icon" style={{ width: "20px", marginRight: "8px" }} />
                                    <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
                                        Pending RAGs ({filteredPendingRAGs.length})
                                    </Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                {filteredPendingRAGs.length > 0 && (
                                    <RAGRequestsTable rags={filteredPendingRAGs} resourceStatuses={updates}/>
                                )}
                            </AccordionDetails>
                        </Accordion>
                        <Accordion
                            defaultExpanded={true}
                            elevation={0}
                            sx={{ borderRadius: "8px", mb: "16px", "&:before": { display: "none" }, bgcolor: "#fafafa" }}
                            disabled={filteredActiveRAGs.length === 0}
                        >
                            <AccordionSummary expandIcon={<ExpandMore />}>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <img src="/assets/tickcircle.svg" alt="Active Icon" style={{ width: "20px", marginRight: "8px" }}/>
                                    <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
                                        Active RAGs ({filteredActiveRAGs.length})
                                    </Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                {filteredActiveRAGs.length > 0 && (
                                    <RAGRequestsTable rags={filteredActiveRAGs} resourceStatuses={updates}/>
                                )}
                            </AccordionDetails>
                        </Accordion>

                        <Accordion
                            defaultExpanded={filteredRejectedRAGs.length > 0 ? true : false}
                            elevation={0}
                            sx={{ borderRadius: "8px", "&:before": { display: "none" }, bgcolor: "#fafafa" }}
                            disabled={filteredRejectedRAGs.length === 0}
                        >
                            <AccordionSummary expandIcon={<ExpandMore />}>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <img src="/assets/reject.svg" alt="Rejected Icon" style={{ width: "20px", marginRight: "8px" }} />
                                    <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
                                        Rejected RAGs ({filteredRejectedRAGs.length})
                                    </Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                {filteredRejectedRAGs.length > 0 && (
                                    <RAGRequestsTable rags={filteredRejectedRAGs} resourceStatuses={updates}/>
                                )}
                            </AccordionDetails>
                        </Accordion>
                    </Box>
                </Container>
            </Box>
        </Box>
    );
};

export default RAGRequestsPage;